import { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { Button } from '../components/ui/button.jsx';
import { Badge } from '../components/ui/badge.jsx';
import {
  Settings,
  Play,
  Pause,
  RotateCcw,
  TrendingDown,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Clock,
  Zap,
  Lightbulb,
  Activity,
  Shield,
  Layers,
  SlidersHorizontal,
  Calendar,
  Bell,
  Trash2
} from 'lucide-react';import { Switch } from '../components/ui/switch.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select.jsx';
import { toast } from 'sonner';
import RightsizingRecommendations from '../components/RightsizingRecommendations';
import RightsizingStatus from '../components/RightsizingStatus';
import RightsizingSettings from '../components/RightsizingSettings';
import SLOTargetsForm from '../components/SLOTargetsForm.jsx';
import SLOPrometheusSettings from '../components/SLOPrometheusSettings.jsx';
import RightsizingPolicies from '../components/RightsizingPolicies';
import OptimizationVisualization from '../components/OptimizationVisualization';
import AutoOptimizeToggle from '../components/AutoOptimizeToggle';
import MLActionApproval from '../components/MLActionApproval';
import OptimizationHistory from '../components/OptimizationHistory';
import RightsizingProfileSelector from '../components/Rightsizing/RightsizingProfileSelector';
import DimensionTogglesPanel from '../components/Rightsizing/DimensionTogglesPanel';
import MaintenanceWindowEditor from '../components/Rightsizing/MaintenanceWindowEditor';
import CleanupConfirmationModal from '../components/Rightsizing/CleanupConfirmationModal';
import RecentSpikesPanel from '../components/Rightsizing/RecentSpikesPanel';
import AlertChannelsPanel from '../components/Alerts/AlertChannelsPanel';
import { useSavings } from '../context/SavingsContext';

function Rightsizing() {
  const [clusters, setClusters] = useState([]);
  const [selectedCluster, setSelectedCluster] = useState(null);
  // Live cluster id for delayed callbacks (the stale-cache follow-up fires
  // 12s later from a closure — without this it could overwrite the list with
  // a previously-selected cluster's data after a cluster switch).
  const selectedClusterIdRef = useRef(null);
  const [recommendations, setRecommendations] = useState([]);
  const [rightsizingStatus, setRightsizingStatus] = useState({});
  const [loading, setLoading] = useState(true);
  // Separate from page-level `loading` (which gates cluster fetch). Without this,
  // the rec list briefly renders the "No Recommendations Available" empty state
  // while the fetch is in flight — misleading on slow clusters/connections.
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('recommendations');
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [autoOptimizeEnabled, setAutoOptimizeEnabled] = useState(false);
  const [executionMode, setExecutionMode] = useState('manual');
  const { realizedSavings, refreshSavings } = useSavings();

  // Honor `?tab=...` deep links — used by the "Apply this template" hint on
  // the Recommendations page so a click lands on the Policies tab directly.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    const allowedTabs = ['recommendations', 'status', 'visualization', 'policies', 'configure', 'alerts', 'settings'];
    if (tabParam && allowedTabs.includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, []);

  useEffect(() => {
    fetchClusters();
  }, []);

  useEffect(() => {
    selectedClusterIdRef.current = selectedCluster?.id || null;
    if (selectedCluster) {
      fetchRecommendations();
      fetchRightsizingStatus();

      // Auto-refresh every 60s. Cheap: the unified endpoint serves from its
      // server-side cache (single find_one when fresh), and the stale-follow-up
      // logic in fetchRecommendations collects recomputed payloads without a
      // manual page reload. 5 min here stacked on the server's 5-min cache
      // meant up to ~10 min of visible staleness.
      const interval = setInterval(() => {
        fetchRecommendations();
        fetchRightsizingStatus();
      }, 60 * 1000);

      return () => clearInterval(interval);
    }
  }, [selectedCluster]);

  const fetchClusters = async () => {
    try {
      const response = await axios.get(`${API}/clusters`);
      setClusters(response.data);
      if (response.data.length > 0) {
        setSelectedCluster(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to load clusters');
    } finally {
      setLoading(false);
    }
  };

  // userInitiated=true only when fired from the Refresh button. Background
  // fetches (mount, cluster switch, 5-min auto-refresh) stay silent on error
  // because a transient cold-backend / auth-race 5xx briefly flashed
  // "Failed to load rightsizing recommendations" on initial page paint.
  const fetchRecommendations = async (userInitiated = false, forceRecompute = false, staleRetries = 2) => {
    if (!selectedCluster) return;

    // Only flash the loading spinner (which clears the visible table) on a
    // USER-initiated fetch. Background polls — the 60s auto-refresh and the
    // stale-while-revalidate follow-up — update the data silently so the recs
    // don't blink away while you're reading them (reported: page looked like
    // it refreshed every 10-20s and the recommendations were unreadable).
    if (userInitiated) setRecommendationsLoading(true);
    try {
      // When the user explicitly clicks Analyze / Rescan / Refresh Analysis,
      // actually RE-RUN the analysis (dry-run, no apply) so the freshest recs
      // are computed — the buttons previously only re-fetched the stale cache.
      // This regenerates optimization_runs; we then read it back with refresh=1
      // to bypass the unified endpoint's 5-min stale-while-revalidate cache.
      if (forceRecompute) {
        try {
          await axios.post(`${API}/clusters/${selectedCluster.id}/auto-optimize`, { dry_run: true });
        } catch (recomputeErr) {
          console.log('Dry-run recompute failed (showing latest cached run):', recomputeErr?.message);
        }
      }

      let recs = [];
      let intents = [];
      // Unified API is authoritative: if it returns success:true with an empty
      // payload, the cluster genuinely has no recs yet (new cluster, agent just
      // connected, no auto-opt history). Falling through to /rightsizing/analyze
      // in that case is what produced the spurious "Failed to load..." toast,
      // because analyze 404s when no metric history exists yet.
      let unifiedSucceeded = false;

      try {
        const unifiedUrl = `${API}/ml/unified-recommendations/${selectedCluster.id}` + (forceRecompute ? '?refresh=1' : '');
        const unifiedResponse = await axios.get(unifiedUrl);
        if (unifiedResponse.data?.success) {
          unifiedSucceeded = true;
          const data = unifiedResponse.data.data;

          // Stale-while-revalidate follow-up: when the server hands back the
          // OLD cached payload and says it's recomputing in the background
          // (stale + refreshing), come back shortly to collect the fresh one —
          // this is what previously required a manual page reload. Bounded
          // retries so a persistently-failing backend refresh can't loop.
          if (unifiedResponse.data.stale && unifiedResponse.data.refreshing && staleRetries > 0) {
            const cid = selectedCluster.id;
            setTimeout(() => {
              if (selectedClusterIdRef.current === cid) {
                fetchRecommendations(false, false, staleRetries - 1);
              }
            }, 30000);
          }

          intents = (data.intents || []).map(i => ({
            ...i,
            source: 'intent',
            badge: 'Intent',
            is_intent: true,
            workload_name: i.workload_name || i.id?.split('/').pop(),
            namespace: i.namespace || i.id?.split('/')[0],
            potential_monthly_savings: 0,
            status: i.status || 'in_progress'
          }));

          recs = [
            ...(data.pending_actions || []),
            ...(data.live_recommendations || [])
          ];
        }
      } catch (unifiedError) {
        console.log('Unified API not available, falling back to analyze endpoint');
      }

      // Only fall back to /rightsizing/analyze when unified actually failed.
      // An empty-but-successful unified response is a real "no recs yet" answer.
      if (!unifiedSucceeded && recs.length === 0) {
        try {
          const response = await axios.post(`${API}/rightsizing/analyze`, {
            cluster_id: selectedCluster.id,
            days: 7
          });
          recs = response.data.recommendations || [];
        } catch (analyzeError) {
          // 404 = backend has no metric history for this cluster yet (agent just
          // connected, or daemonset_agent source hasn't reported). That's a quiet
          // empty state, not an error. Anything else (5xx, network) we surface.
          const status = analyzeError?.response?.status;
          if (status && status !== 404) {
            throw analyzeError;
          }
          console.log('No rightsizing data available yet (cluster still gathering metrics)');
        }
      }

      // Client-side deduplication (keep first by namespace/workload_name).
      //
      // Intents are appended HERE rather than prepended after the loop. They used to
      // bypass this dedup entirely, which is why one Deployment could render two
      // contradictory cards — an intent and a live recommendation, produced by two
      // different calculators with different floors, buffers and confidence models.
      // They go LAST because they are the weakest lane: the decision loop that
      // produces them is disabled by default (SMOOTH_OPTIMIZATION_ENABLED=false), so
      // an intent is typically a stale snapshot that will never converge, while
      // live_recommendations come from the engine that is actually running. An intent
      // now surfaces only when nothing fresher exists for that workload.
      const seenWorkloads = new Set();
      const dedupedRecs = [];

      for (const rec of [...recs, ...intents]) {
        const key = `${rec.namespace || 'default'}/${rec.workload_name || rec.name}`;
        if (!seenWorkloads.has(key)) {
          seenWorkloads.add(key);

          // Normalize: extract flat current_cpu/recommended_cpu from nested resources if needed
          const normalized = { ...rec };

          // Parse current_cpu from current_resources if not already flat
          if (!normalized.current_cpu && normalized.current_resources) {
            const cpuStr = normalized.current_resources.cpu || normalized.current_resources.cpu_request;
            if (typeof cpuStr === 'string' && cpuStr.endsWith('m')) {
              normalized.current_cpu = parseFloat(cpuStr.replace('m', ''));
            } else if (typeof cpuStr === 'number') {
              normalized.current_cpu = cpuStr;
            }
          }

          // Parse recommended_cpu from recommended_resources if not already flat
          if (!normalized.recommended_cpu && normalized.recommended_resources) {
            const cpuStr = normalized.recommended_resources.cpu || normalized.recommended_resources.cpu_request;
            if (typeof cpuStr === 'string' && cpuStr.endsWith('m')) {
              normalized.recommended_cpu = parseFloat(cpuStr.replace('m', ''));
            } else if (typeof cpuStr === 'number') {
              normalized.recommended_cpu = cpuStr;
            }
          }

          // Derive recommendation_type from action_type if not present
          if (!normalized.recommendation_type && normalized.action_type) {
            if (normalized.action_type === 'scale_up') {
              normalized.recommendation_type = 'upsize';
            } else if (normalized.action_type === 'scale_down') {
              normalized.recommendation_type = 'downsize';
            }
          }

          dedupedRecs.push(normalized);
        }
      }

      // Single deduped list — intents were folded into the loop above so they can
      // no longer produce a second card for a workload that already has one.
      setRecommendations(dedupedRecs);

    } catch (error) {
      console.error('Failed to load rightsizing recommendations:', error);
      // Only surface the red toast when the user explicitly clicked Refresh.
      // Initial mount / cluster-switch / 5-min auto-refresh stay silent — a
      // transient cold-backend or auth-race error on first paint shouldn't
      // greet the user with a scary "Failed to load..." banner; the empty
      // state already communicates "nothing here yet" and the next cycle
      // will repopulate.
      if (userInitiated) {
        toast.error('Failed to load rightsizing recommendations');
      }
    } finally {
      setRecommendationsLoading(false);
    }
  };

  const fetchRightsizingStatus = async () => {
    if (!selectedCluster) return;

    try {
      const response = await axios.get(`${API}/rightsizing/status/${selectedCluster.id}`);
      setRightsizingStatus(response.data);
    } catch (error) {
      console.error('Failed to load rightsizing status:', error);
    }
  };

  const handleApplyRecommendations = async (selectedRecommendations) => {
    if (!selectedCluster || selectedRecommendations.length === 0) return;

    try {
      if (executionMode === 'manual' && !autoOptimizeEnabled) {
        const confirmed = window.confirm(
          `Apply ${selectedRecommendations.length} recommendations?\n\nTotal potential savings: $${selectedRecommendations.reduce((sum, r) => sum + (r.potential_monthly_savings || 0), 0).toFixed(2)}/month`
        );
        if (!confirmed) return;
      }

      // Send a NAMESPACE-QUALIFIED id. A bare workload name is ambiguous whenever
      // the same name exists in more than one namespace (e.g. an `api` Deployment in
      // both `prod` and `staging`), and the server previously resolved that by
      // whichever entry it happened to index last. It accepts both forms; the
      // qualified one removes the guess.
      const recommendationIds = selectedRecommendations.map(
        rec => (rec.namespace ? `${rec.namespace}/${rec.workload_name}` : rec.workload_name)
      );

      await axios.post(`${API}/rightsizing/apply/${selectedCluster.id}`, {
        recommendation_ids: recommendationIds
      });
      toast.success('Rightsizing recommendations applied successfully');
      // Recompute + cache-bypass (same path as the Analyze button): a plain
      // fetch here served the ≤5-min-old cache, which can't yet reflect the
      // apply — that's why the page previously needed a manual reload.
      fetchRecommendations(true, true);
      fetchRightsizingStatus();
      refreshSavings(); // Refresh global savings data
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to apply recommendations';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const handleRollback = async (actionId) => {
    if (!selectedCluster) return;

    try {
      // Get the action to find the rollback_plan_id
      const action = rightsizingStatus.recent_actions?.find(a => a.action_id === actionId);
      if (!action) {
        toast.error('Action not found');
        return;
      }

      const confirmed = window.confirm(
        `Are you sure you want to rollback ${action.workload_name}?\n\nThis will restore the previous resource configuration.`
      );
      if (!confirmed) return;

      const response = await axios.post(
        `${API}/rightsizing/rollback/${actionId}`
      );
      toast.success(response.data.message || 'Rollback queued successfully');
      fetchRightsizingStatus();
      fetchRecommendations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rollback changes');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'idle': { color: 'bg-gray-500', icon: Clock, text: 'Idle' },
      'analyzing': { color: 'bg-blue-500', icon: Zap, text: 'Analyzing' },
      'applying': { color: 'bg-yellow-500', icon: Play, text: 'Applying' },
      'completed': { color: 'bg-green-500', icon: CheckCircle, text: 'Completed' },
      'failed': { color: 'bg-red-500', icon: AlertTriangle, text: 'Failed' },
      'rolling_back': { color: 'bg-orange-500', icon: RotateCcw, text: 'Rolling Back' }
    };

    const config = statusConfig[status] || statusConfig['idle'];
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} text-white`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.text}
      </Badge>
    );
  };

  const calculateTotalSavings = () => {
    return recommendations.reduce((total, rec) => total + (rec.potential_monthly_savings || 0), 0);
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-8 animate-fade-in">
          <div className="space-y-3">
            <div className="skeleton h-10 w-64 rounded-xl"></div>
            <div className="skeleton h-6 w-96 rounded-lg"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-40 rounded-2xl"></div>
            ))}
          </div>
          <div className="skeleton h-16 rounded-2xl"></div>
          <div className="skeleton h-96 rounded-2xl"></div>
        </div>
      </Layout>
    );
  }
  return (
    <Layout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-slate-900 dark:text-white text-3xl font-black tracking-tight mb-2">Workload Rightsizing</h1>
            <p className="text-slate-500 dark:text-text-secondary text-base max-w-2xl">
              AI-driven recommendations based on P95 usage over the last 30 days. Apply fixes to reduce waste without compromising stability.
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => fetchRecommendations(true, true)}
              variant="outline"
              className="flex items-center gap-2 h-10 px-4 rounded-lg bg-white dark:bg-surface-dark text-slate-700 dark:text-text-secondary hover:bg-slate-50 dark:hover:bg-background-dark transition-colors border border-slate-300 dark:border-surface-border shadow-sm"
              id="tour-rescan-btn"
            >
              <RotateCcw className="w-4 h-4" />
              <span className="text-sm font-bold">Rescan</span>
            </Button>
            {/* <Button */}
            {/*   onClick={() => { */}
            {/*     const allRecs = recommendations.filter(r => !r.is_intent); */}
            {/*     if (allRecs.length > 0) { */}
            {/*       handleApplyRecommendations(allRecs); */}
            {/*     } else { */}
            {/*       toast.info('No recommendations to apply'); */}
            {/*     } */}
            {/*   }} */}
            {/*   className="flex items-center gap-2 h-10 px-4 rounded-lg bg-[#2b8cee] text-white hover:bg-blue-600 transition-colors shadow-lg shadow-blue-500/20" */}
            {/*   id="tour-autofix-btn" */}
            {/* > */}
            {/*   <Zap className="w-4 h-4" /> */}
            {/*   <span className="text-sm font-bold">Auto-Fix Selected</span> */}
            {/* </Button> */}
          </div>
        </div>

        {/* Summary Stats Cards - New Design */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* <div */}
          {/*   id="tour-monthly-potential" */}
          {/*   className="rounded-xl bg-white dark:bg-surface-dark p-5 border border-slate-200 dark:border-surface-border shadow-sm relative overflow-hidden group" */}
          {/* > */}
          {/*   <div className="absolute right-0 top-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity"> */}
          {/*     <span className="material-symbols-outlined text-6xl text-[#2b8cee]">savings</span> */}
          {/*   </div> */}
          {/*   <p className="text-slate-500 dark:text-text-secondary text-sm font-medium mb-1">Monthly Savings Potential</p> */}
          {/*   <div className="flex items-baseline gap-2"> */}
          {/*     <p className="text-[#2b8cee] text-3xl font-bold tracking-tight">${calculateTotalSavings().toFixed(2)}</p> */}
          {/*     <span className="text-green-600 dark:text-green-400 text-xs font-medium bg-green-50 dark:bg-green-500/10 border border-green-100 dark:border-green-500/20 px-1.5 py-0.5 rounded flex items-center gap-0.5"> */}
          {/*       <span className="material-symbols-outlined text-[12px]">trending_up</span> 12% */}
          {/*     </span> */}
          {/*   </div> */}
          {/*   <div className="w-full bg-slate-100 dark:bg-surface-border h-1.5 rounded-full mt-4 overflow-hidden"> */}
          {/*     <div className="bg-[#2b8cee] h-full rounded-full" style={{ width: '65%' }} /> */}
          {/*   </div> */}
          {/* </div> */}

          <div className="rounded-xl bg-white dark:bg-surface-dark p-5 border border-slate-200 dark:border-surface-border shadow-sm relative overflow-hidden group">
            <div className="absolute right-0 top-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
              <span className="material-symbols-outlined text-6xl text-orange-400">warning</span>
            </div>
            <p className="text-slate-500 dark:text-text-secondary text-sm font-medium mb-1">Under-provisioned Workloads</p>
            <p className="text-slate-900 dark:text-white text-3xl font-bold tracking-tight">
              {recommendations.filter(r =>
                r.recommendation_type === 'upsize' ||
                r.action_type === 'scale_up' ||
                (r.current_cpu && r.recommended_cpu && r.recommended_cpu > r.current_cpu)
              ).length}
              <span className="text-lg text-slate-400 dark:text-text-secondary font-normal"> Risks</span>
            </p>
            <div className="w-full bg-slate-100 dark:bg-surface-border h-1.5 rounded-full mt-4 overflow-hidden">
              <div className="bg-orange-400 h-full rounded-full" style={{ width: '15%' }} />
            </div>
          </div>

          <div className="rounded-xl bg-white dark:bg-surface-dark p-5 border border-slate-200 dark:border-surface-border shadow-sm relative overflow-hidden group">
            <div className="absolute right-0 top-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
              <span className="material-symbols-outlined text-6xl text-blue-300">inventory_2</span>
            </div>
            <p className="text-slate-500 dark:text-text-secondary text-sm font-medium mb-1">Over-provisioned Workloads</p>
            <p className="text-slate-900 dark:text-white text-3xl font-bold tracking-tight">
              {recommendations.filter(r =>
                r.recommendation_type === 'downsize' ||
                r.action_type === 'scale_down' ||
                (r.current_cpu && r.recommended_cpu && r.recommended_cpu < r.current_cpu)
              ).length}
              <span className="text-lg text-slate-400 dark:text-text-secondary font-normal"> Wastes</span>
            </p>
            <div className="w-full bg-slate-100 dark:bg-surface-border h-1.5 rounded-full mt-4 overflow-hidden">
              <div className="bg-blue-300 h-full rounded-full" style={{ width: '45%' }} />
            </div>
          </div>
        </div>

        {/* Auto-Optimization Control */}
        <AutoOptimizeToggle
          clusters={clusters}
          selectedClusterId={selectedCluster?.id}
          onClusterChange={(id) => {
            const cluster = clusters.find(c => c.id === id);
            setSelectedCluster(cluster);
          }}
          onStatusChange={(enabled) => setAutoOptimizeEnabled(enabled)}
        />

        {/* Actions Bar */}
        <div className="flex justify-end space-x-4">
          <Button
            onClick={() => fetchRecommendations(true, true)}
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
          >
            <Play className="w-4 h-4 mr-2" />
            Analyze
          </Button>
          {/* <Button */}
          {/*   onClick={async () => { */}
          {/*     if (!selectedCluster) return; */}
          {/*     try { */}
          {/*       const res = await axios.get(`${API}/rightsizing/bin-packing/${selectedCluster.id}`); */}
          {/*       const binpackRecs = res.data?.recommendations || []; */}
          {/*       if (!binpackRecs.length) { */}
          {/*         toast.info('No bin packing opportunities detected'); */}
          {/*         return; */}
          {/*       } */}
          {/*       await handleApplyRecommendations(binpackRecs); */}
          {/*     } catch (error) { */}
          {/*       toast.error(error.response?.data?.detail || 'Failed to process bin packing'); */}
          {/*     } */}
          {/*   }} */}
          {/*   className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5" */}
          {/* > */}
          {/*   <Layers className="w-4 h-4 mr-2" /> */}
          {/*   Run Bin Packing */}
          {/* </Button> */}
          <Button
            variant="outline"
            onClick={() => fetchRecommendations(true, true)}
            className="flex items-center"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Refresh Analysis
          </Button>
        </div>

        {/* Summary Cards */}
        {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-6 stagger-fade-in"> */}
        {/*   <Card className="stat-card hover-lift transition-smooth border-0 shadow-lg dark:bg-surface-dark dark:border dark:border-surface-border"> */}
        {/*     <CardContent className="p-6"> */}
        {/*       <div className="flex items-start justify-between"> */}
        {/*         <div className="flex-1"> */}
        {/*           <p className="text-sm font-medium text-slate-600 dark:text-text-secondary mb-3">Total Recommendations</p> */}
        {/*           <p className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent"> */}
        {/*             {recommendations.length} */}
        {/*           </p> */}
        {/*           <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 font-medium">optimization opportunities</p> */}
        {/*         </div> */}
        {/*         <div className="p-4 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl shadow-lg transition-transform hover:scale-110"> */}
        {/*           <Zap className="w-7 h-7 text-white" /> */}
        {/*         </div> */}
        {/*       </div> */}
        {/*     </CardContent> */}
        {/*   </Card> */}
        {/**/}
        {/*   <Card className="stat-card hover-lift transition-smooth border-0 shadow-lg dark:bg-surface-dark dark:border dark:border-surface-border"> */}
        {/*     <CardContent className="p-6"> */}
        {/*       <div className="flex items-start justify-between"> */}
        {/*         <div className="flex-1"> */}
        {/*           <p className="text-sm font-medium text-slate-600 dark:text-text-secondary mb-3">Potential Savings</p> */}
        {/*           <p className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-500 bg-clip-text text-transparent"> */}
        {/*             ${calculateTotalSavings().toFixed(2)} */}
        {/*           </p> */}
        {/*           <p className="text-xs text-green-600 dark:text-green-400 mt-2 font-medium">per month</p> */}
        {/*         </div> */}
        {/*         <div className="p-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-lg transition-transform hover:scale-110"> */}
        {/*           <DollarSign className="w-7 h-7 text-white" /> */}
        {/*         </div> */}
        {/*       </div> */}
        {/*     </CardContent> */}
        {/*   </Card> */}
        {/**/}
        {/*   <Card className="stat-card metric-card border-0 shadow-lg dark:bg-surface-dark dark:border dark:border-surface-border"> */}
        {/*     <CardContent className="p-6"> */}
        {/*       <div className="flex items-start justify-between"> */}
        {/*         <div className="flex-1"> */}
        {/*           <p className="text-sm font-medium text-slate-600 dark:text-text-secondary mb-3">Saved Till Now</p> */}
        {/*           <p className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-500 bg-clip-text text-transparent"> */}
        {/*             ${realizedSavings.toFixed(2)} */}
        {/*           </p> */}
        {/*           <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-2 font-medium">✓ Realized monthly savings */}
        {/*           </p> */}
        {/*         </div> */}
        {/*         <div className="p-4 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg"> */}
        {/*           <CheckCircle className="w-7 h-7 text-white" /> */}
        {/*         </div> */}
        {/*       </div> */}
        {/*     </CardContent> */}
        {/*   </Card> */}
        {/* </div> */}

        {/* Tab Navigation */}
        <div className="bg-white dark:bg-surface-dark rounded-xl shadow-sm border border-slate-200 dark:border-surface-border p-2">
          <nav className="flex space-x-2">
            {[
              { id: 'recommendations', label: 'Recommendations', icon: Lightbulb },
              { id: 'status', label: 'Status & History', icon: Activity },
              { id: 'policies', label: 'Policies', icon: Shield },
              { id: 'configure', label: 'Configure', icon: SlidersHorizontal },
              { id: 'alerts', label: 'Alerts', icon: Bell },
              { id: 'settings', label: 'Settings', icon: Settings }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium text-sm flex items-center justify-center space-x-2 transition-all duration-200 ${activeTab === tab.id
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md shadow-blue-200 dark:shadow-blue-500/20'
                    : 'text-slate-600 dark:text-text-secondary hover:bg-slate-100 dark:hover:bg-background-dark hover:text-slate-900 dark:hover:text-white'
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'recommendations' && (
            <div id="tour-rightsizing-list">
              <RightsizingRecommendations
                clusterId={selectedCluster?.id}
                recommendations={recommendations}
                onApply={handleApplyRecommendations}
                onRefresh={() => fetchRecommendations(true)}
                loading={loading || recommendationsLoading}
              />
            </div>
          )}

          {activeTab === 'status' && (
            <div className="space-y-6">
              <RightsizingStatus
                status={rightsizingStatus}
                onRollback={handleRollback}
                onRefresh={fetchRightsizingStatus}
                clusterId={selectedCluster?.id}
              />
              <RecentSpikesPanel clusterId={selectedCluster?.id} limit={50} />
              <OptimizationHistory clusterId={selectedCluster?.id} />
            </div>
          )}

          {activeTab === 'visualization' && (
            <OptimizationVisualization
              clusterId={selectedCluster?.id}
              recommendations={recommendations}
            />
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <RightsizingSettings
                clusterId={selectedCluster?.id}
              />
              <SLOPrometheusSettings
                clusterId={selectedCluster?.id}
              />
              <SLOTargetsForm
                clusterId={selectedCluster?.id}
              />
            </div>
          )}

          {activeTab === 'policies' && (
            <RightsizingPolicies
              clusterId={selectedCluster?.id}
            />
          )}

          {activeTab === 'configure' && (
            <div className="space-y-6">
              <RightsizingProfileSelector
                clusterId={selectedCluster?.id}
                policyId={'default'}
              />
              <DimensionTogglesPanel
                clusterId={selectedCluster?.id}
                policyId={'default'}
              />
              <MaintenanceWindowEditor
                clusterId={selectedCluster?.id}
              />
              <div className="border-2 border-red-200 rounded-lg p-4 bg-red-50 dark:bg-red-950/20">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-red-800 dark:text-red-300 flex items-center gap-2">
                      <Trash2 className="w-4 h-4" /> Cleanup applied patches
                    </h3>
                    <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                      Revert all eligible workloads to their last-known-good resource specs.
                      OOM-protected and critical-priority workloads are skipped automatically.
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    onClick={() => setCleanupOpen(true)}
                    disabled={!selectedCluster?.id}
                  >
                    Review revert plan
                  </Button>
                </div>
              </div>
              <CleanupConfirmationModal
                clusterId={selectedCluster?.id}
                open={cleanupOpen}
                onClose={() => setCleanupOpen(false)}
                onSubmitted={() => {
                  toast.success('Revert actions queued. Watch the action history for progress.');
                  fetchRightsizingStatus?.();
                }}
              />
            </div>
          )}

          {activeTab === 'alerts' && (
            <AlertChannelsPanel clusterId={selectedCluster?.id} />
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Rightsizing;
